import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import clsx from "clsx"
import React, { useContext, useEffect, useState } from "react"
import { useQueryClient } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import AchievementsIcon from "~/assets/Icons/achievements-icon.svg?react"
import CoursesLessonsIcon from "~/assets/Icons/course-icon.svg?react"
import DesktopTabletIcon from "~/assets/Icons/desktop-tablet-icon.svg?react"
import LabsIcon from "~/assets/Icons/labs.svg?react"
import DocsIcon from "~/assets/Icons/new-docs-icon.svg?react"
import NonLinearIcon from "~/assets/Icons/non-linear-icon.svg?react"
import ProfileIcon from "~/assets/Icons/profile-icon.svg?react"
import ShortcodesIcon from "~/assets/Icons/short-codes.svg?react"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import CategoryContext from "~/contexts/Category"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "../../Common/Box"
import useStyles from "./styles"

const CategoryChannelDetails = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const openErrorSnackbar = useOpenErrorSnackbar()
  const { setPageSubTitle } = useContext(AppContext)

  const [iconListState, setIconListState] = useState([])

  const {
    category,
    setInitialDetailsContext,
    debouncedDetails,
    setDebouncedDetails,
    catDetailsValues,
    setCatDetailsValues,
    updateCategoryMutation,

    categoryCourses,
    categoryLearningPaths,
    categoryArticles,
  } = useContext(CategoryContext)

  const { refetchCategory } = useContext(CategoryContext)

  useEffect(() => {
    if (category?.id) {
      setCatDetailsValues({
        name: category?.name || "",
        published_state: category.published_state || "draft",
        description: category.description || "",
        headline: category.headline || "",
        type: category.type || "topic",
        partner_status: category.partner_status || null,
        link: category.link || null,

        channel_details: {
          channel_card: category?.channel_details?.channel_card || "",
          banner: {
            title: category?.channel_details?.banner?.title || "",
            image: category?.channel_details?.banner?.image || "",
          },
          hero: {
            title: category?.channel_details?.hero?.title || "",
            description: category?.channel_details?.hero?.description || "",
            type: category?.channel_details?.hero?.type || "",
            videoSrc: category?.channel_details?.hero?.videoSrc || "",
          },
          overview: {
            description: category?.channel_details?.overview?.description || "",
            iconList: category?.channel_details?.overview?.iconList || [],
          },
          hide_instructors:
            category?.channel_details?.hide_instructors || false,
        },
      })
      setInitialDetailsContext({
        name: category?.name || "",
        published_state: category.published_state || "draft",
        description: category.description || "",
        headline: category.headline || "",
        type: category.type || "topic",
        partner_status: category.partner_status || null,
        link: category.link || null,

        channel_details: {
          channel_card: category?.channel_details?.channel_card || "",
          banner: {
            title: category?.channel_details?.banner?.title || "",
            image: category?.channel_details?.banner?.image || "",
          },
          hero: {
            title: category?.channel_details?.hero?.title || "",
            description: category?.channel_details?.hero?.description || "",
            type: category?.channel_details?.hero?.type || "",
            videoSrc: category?.channel_details?.hero?.videoSrc || "",
          },
          overview: {
            description: category?.channel_details?.overview?.description || "",
            iconList: category?.channel_details?.overview?.iconList || [],
          },
          hide_instructors:
            category?.channel_details?.hide_instructors || false,
        },
      })
    }
  }, [category])

  const bannerTitleError =
    debouncedDetails?.banner?.title === "" ||
    debouncedDetails?.banner?.title?.length > 50

  const debouncedLocalValues = useDebounce(catDetailsValues, 200)

  useEffect(() => {
    setDebouncedDetails(catDetailsValues)
  }, [debouncedLocalValues])

  useEffect(() => {
    setPageSubTitle("")
  }, [])

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image =
      uploadRes === null
        ? null
        : {
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            original_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          }

    const orderedCourses = categoryCourses.map((c, i) => ({
      course_id: c.id,
      order: i,
      course_container_id: c.container_id,
    }))
    const orderedLearningPaths = categoryLearningPaths.map((lp, i) => ({
      learning_path_id: lp.id,
      order: i,
    }))

    const orderedDocs = categoryArticles
      .filter((x) => x?.type === "doc")
      .map((y, i) => {
        return {
          article_id: y?.id,
          order: i,
        }
      })
    const orderedUpdates = categoryArticles
      .filter((x) => x?.type === "update")
      .map((y, i) => {
        return {
          article_id: y?.id,
          order: i,
        }
      })

    const orderedArticles = orderedDocs.concat(orderedUpdates)
    let data = {
      ...debouncedDetails,
      articles: orderedArticles,
      courses: orderedCourses,
      learning_paths: orderedLearningPaths,
      channel_details: {
        channel_card:
          type === "channelCard"
            ? image === null
              ? null
              : JSON.stringify(image)
            : catDetailsValues.channel_details.channel_card,
        banner: {
          image:
            type === "bannerImage"
              ? image === null
                ? null
                : JSON.stringify(image)
              : catDetailsValues.channel_details.banner.image,
          title: catDetailsValues.channel_details.banner.title,
        },
        hero: {
          title: catDetailsValues.channel_details.hero.title,
          description: catDetailsValues.channel_details.hero.description,
          type: catDetailsValues.channel_details.hero.type,
          videoSrc: catDetailsValues.channel_details.hero.videoSrc,
        },
        overview: {
          description: catDetailsValues.channel_details.overview.description,
          iconList: catDetailsValues.channel_details?.overview?.iconList,
        },
        hide_instructors: catDetailsValues?.channel_details?.hide_instructors,
      },
    }

    updateCategoryMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("category", res.data).then((serializedData) => {
          return queryClient.setQueryData("categories", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                x[type] = serializedData[type]
                return x
              }
            })
            return [...newArray]
          })
          // return
        })
        refetchCategory()
        loadingAction(false)
      },
      onError: (err) => {
        console.error(err)
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Thumbnail")
      },
    })
  }

  const deleteIcon = (obj) => {
    const newArray = iconListState.filter((x, index) => x.name !== obj.name)
    setCatDetailsValues((oldState) => ({
      ...oldState,
      channel_details: {
        channel_card: category?.channel_details?.channel_card || "",
        banner: {
          title: catDetailsValues?.channel_details?.banner?.title,
          image: catDetailsValues?.channel_details?.banner?.image,
        },
        hero: {
          title: catDetailsValues?.channel_details?.hero?.title,
          description: catDetailsValues?.channel_details?.hero?.description,
          type: catDetailsValues?.channel_details?.hero?.type,
          videoSrc: catDetailsValues?.channel_details?.hero?.videoSrc,
        },
        overview: {
          description: catDetailsValues?.channel_details?.overview?.description,
          iconList: [...newArray],
        },
        hide_instructors: catDetailsValues?.channel_details?.hide_instructors,
      },
    }))
    setIconListState([...newArray])

    return
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }
  function onDragEnd(result) {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }
    let newArray = [...iconListState]

    const iconsReordered = reorder(
      newArray,
      result.source.index,
      result.destination.index,
    )
    setCatDetailsValues((oldState) => ({
      ...oldState,
      channel_details: {
        channel_card: category?.channel_details?.channel_card || "",
        banner: {
          title: catDetailsValues?.channel_details?.banner?.title,
          image: catDetailsValues?.channel_details?.banner?.image,
        },
        hero: {
          title: catDetailsValues?.channel_details?.hero?.title,
          description: catDetailsValues?.channel_details?.hero?.description,
          type: catDetailsValues?.channel_details?.hero?.type,
          videoSrc: catDetailsValues?.channel_details?.hero?.videoSrc,
        },
        overview: {
          description: catDetailsValues?.channel_details?.overview?.description,
          iconList: [...iconsReordered],
        },
        hide_instructors: catDetailsValues?.channel_details?.hide_instructors,
      },
    }))

    setIconListState(iconsReordered)
  }

  const group = [
    { icon: "achievements", name: "Achievements" },
    { icon: "linear", name: "Linear" },
    { icon: "non-linear", name: "Non-Linear" },
    { icon: "shortcodes", name: "Short Codes" },
    { icon: "courses", name: "Courses" },
    { icon: "self-paced", name: "Self-Paced" },
    { icon: "desktop", name: "DesktopTablet" },
    { icon: "labs", name: "Labs" },
    { icon: "docs", name: "Docs" },
  ]
  // The best way and thin
  const iconGlossary = [
    { name: "achievements", icon: <AchievementsIcon className="w-5 h-5" /> },
    {
      name: "linear",
      icon: <NonLinearIcon className="w-5 h-5" />,
    },
    {
      name: "non-linear",
      icon: <NonLinearIcon className="w-5 h-5" />,
    },
    {
      name: "shortcodes",
      icon: <ShortcodesIcon className="w-5 h-5" />,
    },
    {
      name: "courses",
      icon: <CoursesLessonsIcon className="w-5 h-5" />,
    },
    {
      name: "self-paced",
      icon: <ProfileIcon className="w-5 h-5" />,
    },
    {
      name: "desktop",
      icon: <DesktopTabletIcon className="w-5 h-5" />,
    },
    {
      name: "labs",
      icon: <LabsIcon className="w-5 h-5" />,
    },
    {
      name: "docs",
      icon: <DocsIcon className="w-5 h-5" />,
    },
  ]

  useEffect(() => {
    if (
      category?.channel_details?.overview?.iconList?.length >
      iconListState.length
    ) {
      setIconListState(category.channel_details.overview.iconList)
    }
  }, [category])
  //
  return (
    <Box className={clsx(classes.container, "overflow-scroll h-full pb-32")}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Channel Details
      </Typography>
      <Typography variant="h3" color="primary" className={classes.title}>
        Banner Section
      </Typography>
      <Typography color="secondary" className={classes.inputLabel}>
        Banner/Channel Title
      </Typography>
      <TextField
        value={catDetailsValues?.channel_details?.banner?.title || ""}
        onChange={(e) => {
          setCatDetailsValues((oldState) => ({
            ...oldState,
            channel_details: {
              ...oldState.channel_details,
              banner: {
                ...oldState.channel_details.banner,
                title: e.target.value,
              },
            },
          }))
        }}
        style={{ marginBottom: "10px" }}
        InputProps={{
          classes: {
            root: classes.inputBackgroundColor,
          },
        }}
        variant="filled"
        size="small"
        fullWidth
      />
      {/* <Box display="flex" justifyContent="space-between" mb={1}> */}
      <Box className={classes.thumbnailSectionContainer}>
        <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Banner Image
            <Tooltip title="Long banner on top of channel">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <>
            <div className="bg-gray-300">
              <ThumbnailUpload
                title="Drop small image here"
                cloudinaryUploadPreset={
                  import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                }
                value={
                  category?.channel_details?.banner?.image
                    ? JSON.parse(category?.channel_details?.banner?.image)
                    : ""
                }
                onUpload={setThumbnail}
                noCrop={true}
                type="bannerImage"
              />
            </div>
            {Boolean(category?.channel_details?.banner?.image) && (
              <Typography variant="h4" className={classes.mediaKey}>
                {
                  JSON.parse(category?.channel_details?.banner?.image)
                    ?.original_filename
                }
                .{JSON.parse(category?.channel_details?.banner?.image)?.format}
              </Typography>
            )}
          </>
        </Box>
        <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Channel Card Image
            <Tooltip title="Card Image for Channels Page">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <>
            <div className="bg-gray-300">
              <ThumbnailUpload
                title="Drop small image here"
                cloudinaryUploadPreset={
                  import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                }
                value={
                  category?.channel_details?.channel_card
                    ? JSON.parse(category?.channel_details?.channel_card)
                    : ""
                }
                onUpload={setThumbnail}
                noCrop={true}
                type="channelCard"
              />
            </div>
            {Boolean(category?.channel_details?.channel_card) && (
              <Typography variant="h4" className={classes.mediaKey}>
                {
                  JSON.parse(category?.channel_details?.channel_card)
                    ?.original_filename
                }
                .{JSON.parse(category?.channel_details?.channel_card)?.format}
              </Typography>
            )}
          </>
        </Box>
      </Box>

      <Box width="100%" className="mt-2">
        <Typography variant="h3" color="primary" className={classes.title}>
          Hero Section
          <Tooltip title="Video/ Description section on All tab of a channel">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Typography>

        <div className={classes.twoFieldContainer}>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Typography color="secondary" className={classes.inputLabel}>
              Hero Title
            </Typography>
            <TextField
              value={catDetailsValues?.channel_details?.hero?.title || ""}
              onChange={(e) => {
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  channel_details: {
                    ...oldState.channel_details,
                    hero: {
                      ...oldState.channel_details.hero,
                      title: e.target.value,
                    },
                  },
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Typography color="secondary" className={classes.inputLabel}>
              Hero Description
            </Typography>
            <TextField
              value={catDetailsValues?.channel_details?.hero?.description || ""}
              onChange={(e) => {
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  channel_details: {
                    ...oldState.channel_details,
                    hero: {
                      ...oldState.channel_details.hero,
                      description: e.target.value,
                    },
                  },
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="medium"
              multiline={true}
              minRows="6"
              fullWidth
            />
          </Box>
        </div>
      </Box>
      <div className={classes.twoFieldContainerMdReveresed}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography color="secondary" className="mt-1 font-bold">
            Hero Type
            <Tooltip title="String above Video Title">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <RadioGroup
            className="flex flex-row gap-4 mb-3"
            value={
              catDetailsValues?.channel_details?.hero?.type
                ? catDetailsValues?.channel_details?.hero?.type
                : ""
            }
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                channel_details: {
                  ...oldState.channel_details,
                  hero: {
                    ...oldState.channel_details.hero,
                    type: e.target.value,
                  },
                },
              }))
            }}
          >
            <Box
              key={
                catDetailsValues?.channel_details?.hero?.type
                  ? catDetailsValues?.channel_details?.hero?.type
                  : ""
              }
            >
              <FormControlLabel
                label="TL;DR"
                value="tldr"
                control={
                  <Radio
                    value="tldr"
                    checked={
                      catDetailsValues?.channel_details?.hero?.type === "tldr"
                    }
                    onClick={() => {
                      // eslint-disable-next-line babel/no-unused-expressions
                      catDetailsValues?.channel_details?.hero?.type ===
                        "tldr" &&
                        setCatDetailsValues((oldState) => ({
                          ...oldState,
                          channel_details: {
                            ...oldState.channel_details,
                            hero: {
                              ...oldState.channel_details.hero,
                              type: "",
                            },
                          },
                        }))
                    }}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Course"
                value="course"
                control={
                  <Radio
                    value="course"
                    checked={
                      catDetailsValues?.channel_details?.hero?.type === "course"
                    }
                    onClick={() => {
                      // eslint-disable-next-line babel/no-unused-expressions
                      catDetailsValues?.channel_details?.hero?.type ===
                        "course" &&
                        setCatDetailsValues((oldState) => ({
                          ...oldState,
                          channel_details: {
                            ...oldState.channel_details,
                            hero: {
                              ...oldState.channel_details.hero,
                              type: "",
                            },
                          },
                        }))
                    }}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Badge"
                value="badge"
                control={
                  <Radio
                    value="badge"
                    checked={
                      catDetailsValues?.channel_details?.hero?.type === "badge"
                    }
                    onClick={() => {
                      // eslint-disable-next-line babel/no-unused-expressions
                      catDetailsValues?.channel_details?.hero?.type ===
                        "badge" &&
                        setCatDetailsValues((oldState) => ({
                          ...oldState,
                          channel_details: {
                            ...oldState.channel_details,
                            hero: {
                              ...oldState.channel_details.hero,
                              type: "",
                            },
                          },
                        }))
                    }}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Certification"
                value="certification"
                control={
                  <Radio
                    value="certification"
                    checked={
                      catDetailsValues?.channel_details?.hero?.type ===
                      "certification"
                    }
                    onClick={() => {
                      // eslint-disable-next-line babel/no-unused-expressions
                      catDetailsValues?.channel_details?.hero?.type ===
                        "certification" &&
                        setCatDetailsValues((oldState) => ({
                          ...oldState,
                          channel_details: {
                            ...oldState.channel_details,
                            hero: {
                              ...oldState.channel_details.hero,
                              type: "",
                            },
                          },
                        }))
                    }}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="New"
                value="new"
                control={
                  <Radio
                    value="new"
                    checked={
                      catDetailsValues?.channel_details?.hero?.type === "new"
                    }
                    onClick={() => {
                      // eslint-disable-next-line babel/no-unused-expressions
                      catDetailsValues?.channel_details?.hero?.type === "new" &&
                        setCatDetailsValues((oldState) => ({
                          ...oldState,
                          channel_details: {
                            ...oldState.channel_details,
                            hero: {
                              ...oldState.channel_details.hero,
                              type: "",
                            },
                          },
                        }))
                    }}
                  />
                }
              />
            </Box>
          </RadioGroup>
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Video Src
            <Tooltip title="Link for iframe - Wistia Example: https://fast.wistia.net/embed/iframe/3cqz5so1is?seo=true&videoFoam=true">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <TextField
            value={catDetailsValues?.channel_details?.hero?.videoSrc || ""}
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                channel_details: {
                  ...oldState.channel_details,
                  hero: {
                    ...oldState.channel_details.hero,
                    videoSrc: e.target.value,
                  },
                },
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
        </Box>
      </div>
      <Box width="100%" className="mt-2">
        <Typography variant="h3" color="primary" className={classes.title}>
          Overview Section
        </Typography>
        <div className={classes.twoFieldContainer}>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Typography color="secondary" className={classes.inputLabel}>
              Overview Description
            </Typography>
            <TextField
              value={catDetailsValues?.channel_details?.overview?.description}
              onChange={(e) => {
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  channel_details: {
                    ...oldState.channel_details,
                    overview: {
                      ...oldState.channel_details.overview,
                      description: e.target.value,
                    },
                  },
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="medium"
              multiline={true}
              minRows="6"
              fullWidth
            />
          </Box>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Typography color="secondary" className={classes.inputLabel}>
              Icon List
              <Tooltip title="Click radio to add to overview section">
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </Typography>
            <FormGroup>
              {group?.map((x, i) => {
                return (
                  <Box key={i}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            iconListState?.find(
                              (obj) => obj.name === x.name,
                            ) !== undefined
                          }
                          onChange={(e, val) => {
                            if (
                              iconListState?.find((obj) => obj.name === x.name)
                            ) {
                              deleteIcon(x)
                            } else {
                              val !== null &&
                                group.some((y) => x.name === y.name) &&
                                setIconListState([...iconListState, x])
                              setCatDetailsValues((oldState) => ({
                                ...oldState,
                                channel_details: {
                                  ...oldState.channel_details,
                                  overview: {
                                    ...oldState.channel_details.overview,
                                    iconList: [...iconListState, x],
                                  },
                                },
                              }))
                            }
                          }}
                          name={x.name}
                        />
                      }
                      label={
                        <React.Fragment>
                          <div className="flex flex-nowrap w-full text-black stroke-current fill-current gap-1">
                            {
                              iconGlossary.find((obj) => obj.name === x.icon)
                                .icon
                            }
                            <p className="whitespace-nowrap">{x.name}</p>
                          </div>
                        </React.Fragment>
                      }
                    />
                  </Box>
                )
              })}
            </FormGroup>
          </Box>

          <div className="mt-8">
            <Typography variant="h3" color="primary" className={classes.title}>
              Instructors Section
            </Typography>

            <Box display="flex">
              <Typography color="secondary" className={classes.inputLabel}>
                Hide Instructors
              </Typography>
              <Switch
                onChange={(e) => {
                  setCatDetailsValues((oldState) => ({
                    ...oldState,
                    channel_details: {
                      ...oldState.channel_details,
                      hide_instructors: e.target.checked,
                    },
                  }))
                }}
                checked={Boolean(
                  catDetailsValues?.channel_details?.hide_instructors,
                )}
              />
            </Box>
          </div>
        </div>
      </Box>
    </Box>
  )
}

export default CategoryChannelDetails

{
  /* <Autocomplete
        options={group || []}
        renderInput={(params) => <TextField {...params} variant="filled" />}
        value={autocompleteValue}
        onChange={(e, val) => {
          if (
            iconListState.find((obj) => obj.name === val.name) === undefined &&
            iconListState.find((obj) => obj.name === val) === undefined
          ) {
            setAutocompleteValue(val)
            if (val !== null && group.some((x) => x.name === val.name)) {
              setIconListState([...iconListState, val])
            }
            setCatDetailsValues((oldState) => ({
              ...oldState,
              channel_details: {
                ...oldState.channel_details,
                overview: {
                  ...oldState.channel_details.overview,
                  iconList: [...iconListState, val],
                },
              },
            }))
          } else {
            openSnackBar({ message: "Only One of each Icon type" })
          }
        }}
        getOptionLabel={(option) => option.name || option}
        freeSolo
        openOnFocus
        onInputChange={(e, val) => {
          setTextValue(val)
        }}
        inputValue={textValue}
        style={{
          width: "100%",
          backgroundColor: "#F4F4F4",
          borderRadius: "4px",
        }}
        onKeyPress={(e) => {
          if (
            e.key === "Enter" &&
            (textValue !== "" || autocompleteValue !== "")
          ) {
            //check if we are using the same work with capital letters or spaces
            if (
              group.find(
                (y) =>
                  y.name.replace(/\s+/g, "").toLowerCase() ===
                  textValue.replace(/\s+/g, "").toLowerCase()
              )
            ) {
              // upon finding a match in input tag vs existing tag
              setAutocompleteValue("")
              setTextValue("")
            } else {
              const found = group.find(
                (y) =>
                  y.name.replace(/\s+/g, "").toLowerCase() ===
                  textValue.replace(/\s+/g, "").toLowerCase()
              )
              if (found) {
                setAutocompleteValue("")
                setTextValue("")
              } else {
                setAutocompleteValue("")
                setTextValue("")
              }
            }
          }
        }}
        size="small"
        classes={{
          inputRoot: classes.autocompleteRoot,
        }}
      /> */
}
{
  /* <Box
        display="flex"
        flexDirection="column"
        flexWrap=""
        style={{ height: "100%" }}
        marginBottom="16px"
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="icons">
            {(provided) => (
              <div
                className="icons"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {iconListState?.map((x, i) => {
                  let CurrIcon = iconGlossary.find(
                    (obj) => obj.name === x.icon
                  ).icon

                  return (
                    <Draggable draggableId={x.name} index={i} key={i}>
                      {(provided) => (
                        <div
                          key={i}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          tabIndex={-1}
                        >
                          <Chip
                            key={i}
                            style={{
                              marginRight: "8px",
                              marginTop: "16px",
                              paddingLeft: "16px",
                              display: "flex",
                              justifyContent: "left",
                              alignContent: "center",
                            }}
                            classes={{
                              root:
                                x?.description.length > 0
                                  ? classes.chipRootFilled
                                  : classes.chipRoot,
                            }}
                            icon={CurrIcon}
                            label={
                              x?.description?.length > 0
                                ? x.name + ": " + x?.description || ""
                                : x.name + ":" || ""
                            }
                            onDelete={() => deleteIcon(i)}
                            color={
                              x?.description?.length > 0
                                ? "primary"
                                : "secondary"
                            }
                            size="small"
                            onClick={() => {
                              chipIndexOpen !== i
                                ? setChipIndexOpen(i)
                                : setChipIndexOpen(-1)
                            }}
                          />
                          <div
                            className={
                              chipIndexOpen === i ? "block mt-1" : "hidden"
                            }
                            tabIndex={-1}
                          >
                            <CounterHeader
                              title="Icon Text"
                              value={
                                debouncedDetails?.channel_details?.overview
                                  ?.iconList[i]?.description
                              }
                              max={25}
                            />
                            <TextField
                              value={
                                catDetailsValues?.channel_details?.overview
                                  ?.iconList[i]?.description
                              }
                              onKeyDown={(e) => {
                                if (e.code === "Enter") {
                                  setChipIndexOpen(-1)
                                }
                              }}
                              tabIndex={chipIndexOpen === i ? 0 : -1}
                              onChange={(e) => {
                                const updatedIconList = iconListState.map(
                                  (item, index) => {
                                    if (index === i) {
                                      return {
                                        ...item,
                                        description: e.target.value,
                                      }
                                    } else {
                                      return item
                                    }
                                  }
                                )
                                setCatDetailsValues((oldState) => ({
                                  ...oldState,
                                  channel_details: {
                                    ...oldState.channel_details,
                                    overview: {
                                      ...oldState.channel_details.overview,
                                      iconList: updatedIconList,
                                    },
                                  },
                                }))
                                setIconListState([...updatedIconList])
                              }}
                              style={{ marginBottom: "10px" }}
                              InputProps={{
                                classes: {
                                  root: classes.inputBackgroundColor,
                                },
                              }}
                              variant="filled"
                              size="small"
                              fullWidth
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  )
                })}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box> */
}
